import {
  LiveDraftTransitionStore,
  useLiveDraftTransitionStore,
} from "@/data-preparation-tool/live-draft-transition-store";
import { Projection } from "@/data-preparation-tool/ui/projection-switch";
import { applySavedView } from "@/features/saved-views/apply-saved-view";
import { useSavedView } from "@/features/saved-views/saved-view-context";
import {
  SceneConversionState,
  useSceneConversionState,
} from "@/hooks/use-scene-conversion-state";
import { isTopDownView } from "@/registration-tools/utils/camera-views";
import {
  changeMode,
  ModeInitialState,
  selectIsSavedViewApplied,
} from "@/store/mode-slice";
import { selectIsProjectEmpty } from "@/store/selections-selectors";
import { AppThunk } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import { useEffect } from "react";
import { getMode } from ".";
import { Mode } from "./mode";

/**
 * Mode to handle the start of the application
 */
export const startMode: Mode = {
  name: "start",

  ModeScene() {
    const dispatch = useAppDispatch();

    const isProjectEmpty = useAppSelector(selectIsProjectEmpty);
    const savedView = useSavedView();
    const liveDraftTransitionState = useLiveDraftTransitionStore();

    const sceneConversionState = useSceneConversionState();

    const { getState } = useAppStore();

    // When app starts
    useEffect(() => {
      if (!sceneConversionState) {
        dispatch(setShowSpinner(true));
        return;
      }

      dispatch(setShowSpinner(false));
      if (sceneConversionState !== SceneConversionState.Ok) {
        dispatch(changeMode("projectConversion"));
      } else if (isProjectEmpty) {
        dispatch(changeMode("createArea"));
      } else if (
        savedView &&
        !(savedView instanceof Error) &&
        // Each view is applied once. Applying the same one again on a later
        // return to the start mode would discard the state the user set since,
        // while a view that has not been applied yet is a new one to open. The
        // thunk records the view it applied, which is what this reads.
        !selectIsSavedViewApplied(savedView)(getState())
      ) {
        dispatch(applySavedView(savedView));
      } else if (liveDraftTransitionState.camera) {
        dispatch(prepareAppStateFromDraftView(liveDraftTransitionState));
      } else {
        dispatch(changeMode("sheet"));
      }
    }, [
      savedView,
      liveDraftTransitionState,
      dispatch,
      isProjectEmpty,
      getState,
      sceneConversionState,
    ]);

    return null;
  },
  ModeOverlay() {
    // TODO: Better loading screen
    return <p>Loading.....</p>;
  },
};

/**
 * Initialize the state of the Live view based on the previously used state in the Draft view for a seamless transition.
 *
 * @param liveDraftTransitionState the data used for the transition between Live and Draft view.
 * @returns a thunk to set the initial state based on the data from the Draft view.
 */
function prepareAppStateFromDraftView({
  camera,
  projection,
  orbitControls,
}: LiveDraftTransitionStore): AppThunk<void> {
  const modeName =
    projection === Projection.twoDimensional &&
    // The sheet mode only supports a top-down view
    isTopDownView(camera)
      ? "sheet"
      : "overview";

  return (dispatch, getState) => {
    const initialModeState: ModeInitialState =
      getMode(modeName).initialState?.compute(getState(), {
        cameras: camera ? [camera] : [],
        controls: orbitControls ? [orbitControls] : [],
      }) ?? {};

    // When coming back from the Draft view, the pano scene filter can't be used.
    if (
      modeName === "overview" &&
      initialModeState.scene === SceneFilter.Pano
    ) {
      initialModeState.scene = SceneFilter.PointCloud;
    }

    dispatch(
      changeMode({
        mode: modeName,
        initialState: initialModeState,
      }),
    );
  };
}
